/**
 * Who a work order's messages reach, and whether they use WhatsApp (#3023).
 *
 * WhatsApp is the primary channel. Reminders and booking messages go to a customer's WhatsApp
 * until a person records that the customer told them they do not use it; from then on the same
 * messages go as text messages. This is that record, reachable from the work order itself (no
 * offer needed), with the same hooks and server permissions as the offer dialog.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useClearNoWhatsApp,
  useRecordNoWhatsApp,
  useSmsRecipients,
  useSmsStatus,
  type SmsRecipientState,
} from '@/features/settings/sms/useSmsIntegration';
import { useWhatsAppStatus } from '@/hooks/useWhatsAppSettings';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import { describeApiError } from '@/lib/describeApiError';

/** The server's permissions for the recipient routes: workshop settings, or editing a work order. */
const RECIPIENT_PERMISSIONS = ['settings.edit_workshop', 'work_orders.update'];

interface NoWhatsAppRecipientsProps {
  workOrderId: string;
}

export function NoWhatsAppRecipients({ workOrderId }: NoWhatsAppRecipientsProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const allowed = useAuthStore((s) => s.hasAnyPermission(RECIPIENT_PERMISSIONS));

  const whatsappStatus = useWhatsAppStatus(allowed);
  const smsStatus = useSmsStatus(allowed);
  const recipients = useSmsRecipients(workOrderId, allowed);
  const record = useRecordNoWhatsApp(workOrderId);
  const clear = useClearNoWhatsApp(workOrderId);
  const [pending, setPending] = useState<SmsRecipientState | null>(null);

  if (!allowed) return null;

  const smsEnabled = smsStatus.data?.enabled ?? false;
  const whatsappEnabled = whatsappStatus.data?.enabled ?? false;

  const run = async (recipient: SmsRecipientState, action: 'record' | 'withdraw') => {
    const target = { partyType: recipient.partyType, partyId: recipient.partyId };
    const failedKey = action === 'record' ? 'smsRecipients.recordFailed' : 'smsRecipients.withdrawFailed';
    try {
      if (action === 'record') {
        await record.mutateAsync(target);
        notify.success(t('smsRecipients.recorded'), t('smsRecipients.recordedDesc', { name: recipient.name }));
      } else {
        await clear.mutateAsync(target);
        notify.success(t('smsRecipients.withdrawn'), t('smsRecipients.withdrawnDesc', { name: recipient.name }));
      }
    } catch (error) {
      notify.error(t(failedKey), describeApiError(error, t, { codePrefix: 'offers.errorCode', fallback: t(failedKey) }));
    }
  };

  const describe = (recipient: SmsRecipientState): { severity: 'success' | 'info' | 'warning'; text: string } => {
    const number = recipient.smsDestinationMasked ?? '';
    if (recipient.optedOut) return { severity: 'warning', text: t('smsRecipients.optedOut', { name: recipient.name }) };
    if (recipient.whatsapp === 'confirmed_absent') {
      return { severity: 'success', text: t('smsRecipients.confirmed', { name: recipient.name, number }) };
    }
    if (recipient.smsDestinationMasked === null) {
      return { severity: 'warning', text: t('smsRecipients.noNumber', { name: recipient.name }) };
    }
    if (recipient.whatsapp === 'stale') return { severity: 'warning', text: t('smsRecipients.stale', { name: recipient.name }) };
    return { severity: 'info', text: t('smsRecipients.usesWhatsapp', { name: recipient.name }) };
  };

  return (
    <Box sx={{ mt: 2 }} data-testid="no-whatsapp-recipients">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" component="h3" sx={{ mr: 1 }}>
          {t('smsRecipients.title')}
        </Typography>
        <Chip
          size="small"
          color={whatsappEnabled ? 'success' : 'default'}
          label={whatsappEnabled ? t('smsRecipients.whatsappOn') : t('smsRecipients.whatsappOff')}
        />
        <Chip
          size="small"
          color={smsEnabled ? 'success' : 'default'}
          label={smsEnabled ? t('smsRecipients.smsOn') : t('smsRecipients.smsOff')}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('smsRecipients.intro')}
      </Typography>

      {recipients.isPending ? (
        <CircularProgress size={20} aria-label={tc('loading')} />
      ) : recipients.isError ? (
        <Alert severity="error">{t('smsRecipients.loadFailed')}</Alert>
      ) : recipients.data.recipients.length === 0 ? (
        <Alert severity="info">{t('smsRecipients.noRecipient')}</Alert>
      ) : (
        <Stack spacing={1}>
          {recipients.data.recipients.map((recipient) => {
            const { severity, text } = describe(recipient);
            const confirmed = recipient.whatsapp === 'confirmed_absent';
            const canRecord =
              !confirmed && !recipient.optedOut && recipient.smsDestinationMasked !== null && smsEnabled;
            return (
              <Alert
                key={`${recipient.partyType}:${recipient.partyId}`}
                severity={severity}
                action={
                  confirmed ? (
                    <Button
                      color="inherit"
                      size="small"
                      disabled={clear.isPending}
                      onClick={() => {
                        void run(recipient, 'withdraw');
                      }}
                    >
                      {t('smsRecipients.withdraw')}
                    </Button>
                  ) : canRecord ? (
                    <Button
                      color="inherit"
                      size="small"
                      disabled={record.isPending}
                      onClick={() => {
                        setPending(recipient);
                      }}
                    >
                      {t('smsRecipients.record')}
                    </Button>
                  ) : undefined
                }
              >
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {recipient.name}
                </Typography>
                {' · '}
                {recipient.roles.map((role) => t(`parties.roles.${role}`)).join(', ')}
                <br />
                {text}
              </Alert>
            );
          })}
          {!smsEnabled && !smsStatus.isPending && (
            <Typography variant="caption" color="text.secondary">
              {t('smsRecipients.smsUnavailable')}
            </Typography>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={t('smsRecipients.confirmTitle')}
        message={pending ? t('smsRecipients.confirmMessage', { name: pending.name, number: pending.smsDestinationMasked ?? '' }) : ''}
        confirmText={t('smsRecipients.record')}
        loading={record.isPending}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => {
          const target = pending;
          setPending(null);
          if (target) void run(target, 'record');
        }}
      />
    </Box>
  );
}
